import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StatusDot } from '../dashboard/schoolEquipmentChrome';
import { TYPE_META, deviceHref, statusDotTone } from './stateMeta';

const StateCell = ({ state, label, reasons }) => {
  const { t } = useTranslation();
  return (
    <span className="inline-flex flex-col items-start gap-1">
      <StatusDot tone={statusDotTone(state)} label={label} />
      {reasons?.length > 0 && (
        <span className="text-[11px] text-warning">
          {reasons.map((reason) => t(`equipment.reasons.${reason.reason}`, { count: reason.count })).join(' · ')}
        </span>
      )}
    </span>
  );
};

/** Плоский список Оборудования по всему парку — вид «все регистраторы разом». */
const DeviceTable = ({ devices }) => {
  const { t } = useTranslation();

  if (devices.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-500">{t('equipment.empty.devices')}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
          <tr>
            <th className="px-3 py-2 font-medium">{t('equipment.table.type')}</th>
            <th className="px-3 py-2 font-medium">{t('equipment.table.name')}</th>
            <th className="px-3 py-2 font-medium">{t('equipment.table.school')}</th>
            <th className="px-3 py-2 font-medium">{t('equipment.table.address')}</th>
            <th className="px-3 py-2 font-medium">{t('equipment.table.connectivity')}</th>
            <th className="px-3 py-2 font-medium">{t('equipment.table.readiness')}</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device) => {
            const route = deviceHref(device, device.routerIp);
            return (
              <tr key={device.id} className="border-t border-white/[0.04] hover:bg-white/[0.04]">
                <td className="px-3 py-3">
                  <span className="inline-flex items-center gap-2 text-xs text-muted-300">
                    <span className="material-icons text-base text-muted-400">
                      {TYPE_META[device.deviceType].icon}
                    </span>
                    {t(`equipment.types.${device.deviceType}`)}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {route ? (
                    <Link to={route} className="font-medium text-white hover:text-brand-200">
                      {device.name || `#${device.id}`}
                    </Link>
                  ) : (
                    <span className="font-medium text-white">{device.name || `#${device.id}`}</span>
                  )}
                </td>
                <td className="px-3 py-3">
                  <span className="block text-muted-200">{device.schoolName}</span>
                  <span className="block text-[11px] text-muted-500">{device.region}</span>
                </td>
                <td className="px-3 py-3 font-mono text-[12px] text-muted-400">
                  {device.target}
                  {device.model ? <span className="block font-sans text-[11px] text-muted-500">{device.model}</span> : null}
                </td>
                <td className="px-3 py-3">
                  <StateCell
                    state={device.connectivity}
                    label={t(`equipment.state.${device.connectivity}`)}
                  />
                </td>
                <td className="px-3 py-3">
                  {device.connectivity === 'not_configured' ? (
                    <span className="text-xs text-muted-500">—</span>
                  ) : (
                    <StateCell
                      state={device.readiness?.state || 'unknown'}
                      label={t(`equipment.readiness.${device.readiness?.state || 'unknown'}`)}
                      reasons={device.readiness?.reasons}
                    />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default DeviceTable;
